import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, euro } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Car, Calendar, MapPin, ShieldAlert, Wallet, CreditCard } from "lucide-react";

const payLabel = (m) => (m === "pay_online" ? "Online con carta" : "Al ritiro (contanti/carta)");

export default function Confirmation() {
  const { code } = useParams();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/bookings/code/${code}`).then((r) => setBooking(r.data)).catch(() => setNotFound(true));
  }, [code]);

  if (notFound) return <Shell><div className="text-center py-20 text-muted-foreground">Prenotazione non trovata.</div></Shell>;
  if (!booking) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div></Shell>;

  const paid = booking.payment_status === "paid";

  return (
    <Shell>
      <div className="max-w-2xl mx-auto fade-up" data-testid="confirmation-page">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-[#10B981]" />
          </div>
          <h1 className="font-display text-3xl font-extrabold uppercase">Prenotazione confermata</h1>
          <p className="text-muted-foreground mt-2">Ti abbiamo inviato il riepilogo. Mostra questo codice al desk.</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#181A22] overflow-hidden">
          <div className="bg-[#E63946] px-6 py-5 flex items-center justify-between">
            <div>
              <div className="text-white/80 text-xs font-mono-tech tracking-widest">CODICE PRENOTAZIONE</div>
              <div className="text-white font-display font-extrabold text-2xl tracking-wider font-mono-tech" data-testid="booking-code">{booking.code}</div>
            </div>
            <Badge className={`border-0 ${paid ? "bg-[#10B981] text-white" : "bg-white/20 text-white"}`}>{paid ? "Pagato" : "Da pagare al ritiro"}</Badge>
          </div>
          <div className="p-6 space-y-4">
            <Info icon={Car} label="Auto" value={booking.car_name} />
            <Info icon={Calendar} label="Periodo" value={`${booking.pickup_date} → ${booking.return_date} (${booking.days} gg)`} />
            <Info icon={MapPin} label="Ritiro / Riconsegna" value={`${booking.pickup_location} / ${booking.return_location}`} />
            <Info icon={booking.payment_method === "pay_online" ? CreditCard : Wallet} label="Pagamento" value={payLabel(booking.payment_method)} />
            <div className="rounded-xl border border-[#3B82F6]/30 bg-[#3B82F6]/5 p-4 flex gap-3">
              <ShieldAlert className="w-5 h-5 text-[#3B82F6] shrink-0" />
              <p className="text-xs text-muted-foreground">Al ritiro verrà trattenuta una cauzione di <b className="text-foreground">{euro(booking.deposit)}</b> sulla carta, restituita per intero a fine noleggio.</p>
            </div>
            <div className="border-t border-white/10 pt-4 flex justify-between items-center">
              <span className="font-semibold">Totale noleggio</span>
              <span className="font-display font-extrabold text-2xl">{euro(booking.total_price)}</span>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link to="/cars"><Button variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid="back-to-fleet">Torna al parco auto</Button></Link>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background grain"><Navbar /><div className="max-w-7xl mx-auto px-6 pt-32 pb-20">{children}</div></div>
);
const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center"><Icon className="w-4 h-4 amber" /></div>
    <div><div className="text-[11px] text-muted-foreground font-mono-tech tracking-wider uppercase">{label}</div><div className="font-medium">{value}</div></div>
  </div>
);
